import { useEffect, useRef } from 'react';
import { FACES, faceFor } from '@engineer-os/chat';
import { useStrings } from '../../state/useLocale';

/**
 * Which face to wear.
 *
 * Everyone already has something — their operator, or the face the room
 * derives from their tag — so this is never a gate, only a change of mind.
 * That is why there is no "none": the row at the top is the face the tag gives
 * you, offered as the way back rather than as an absence.
 *
 * A grid of buttons and nothing else. No search: there are fifty-six, they are
 * all on screen at once, and a search box over something you can see all of is
 * furniture. Arrow keys are not wired up for the same reason Tab is enough on
 * a grid this size, and Escape is the popover's own affair, handled above.
 *
 * The link at the foot is the other half of the answer, and it is why this
 * picker did not have to grow one. The figures are made on the profile screen,
 * where there is room for nine rails and a preview; picking a face here is the
 * quick way to look like something else, and building an operator is the real
 * one. Choosing a face takes the figure off (state/useChat.ts, `setFace`).
 */
export interface FacePickerProps {
  /** The face being worn, whether chosen or derived. */
  current: string | null;
  /** This browser's tag, for the face it falls back to. */
  tag: string | null;
  onPick(face: string | null): void;
}

export function FacePicker({ current, tag, onPick }: FacePickerProps) {
  const s = useStrings().chat;
  const build = useStrings().profile.build;
  const ref = useRef<HTMLDivElement>(null);

  // The popover opens on a click; focus follows it in, so the first Tab is
  // inside the grid rather than back at the top of the panel.
  useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, []);

  const derived = tag ? faceFor(tag) : null;

  return (
    <div ref={ref} className="face-pick">
      <div className="face-grid" role="group" aria-label={s.faces.label}>
        {FACES.map((face) => (
          <button
            key={face}
            type="button"
            className={face === current ? 'face is-on' : 'face'}
            aria-pressed={face === current}
            title={face === derived ? s.faces.byDefault : undefined}
            onClick={() => onPick(face)}
          >
            <span className="emoji" aria-hidden="true">
              {face}
            </span>
          </button>
        ))}
      </div>
      {derived && current !== derived && (
        <button type="button" className="chat-link face-default" onClick={() => onPick(null)}>
          {s.faces.useDefault(derived)}
        </button>
      )}
      <a className="chat-link face-build" href="?profile=me">
        {build.title}
      </a>
    </div>
  );
}

export default FacePicker;
